import {useEffect, useMemo, useState} from 'react';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {Button, theme} from 'antd';
import {FormShell} from 'components/form/FormShell';
import {RangeInput} from 'components/form/RangeInput';
import {OptionType, Select} from 'components/form/Select';
import {TextArea} from 'components/form/TextArea';
import {usePrompt} from 'helpers/useBlocker';
import {observer} from 'mobx-react-lite';
import {browserHistory} from 'routing/browserHistory';
import {appState} from 'store/AppStore';
import {ReservationCreateUpdateDto} from '../../../../../PRMGApiClient';
import {ReservationState} from 'store/reservations/ReservationState';
import {mapCarsToOptions, mapCustomersToOptions} from 'helpers/utils';
import {TextInput} from 'components/form/TextInput';
import {AppRoutePaths} from 'routing/Routes';

export interface ReservationViewModel extends ReservationCreateUpdateDto {
  carMakeId?: number;
  carModelId?: number;
}

export const ReservationEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {token} = theme.useToken();
  const {commonData} = appState;
  const [state] = useState(() => new ReservationState());
  const availableCarsOptions = mapCarsToOptions(state.availableCars);
  const customerOptions = mapCustomersToOptions(state.customers);
  const reservation = state.reservationViewModel;

  const {reservationId, vehicleId} = useParams();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: {errors, isDirty},
  } = useForm<ReservationViewModel>({
    mode: 'onTouched',
    defaultValues: {
      ...reservation,
      begins: reservation.begins ?? new Date(),
      ends: reservation.ends ?? new Date(),
    },
  });
  const startDate = watch('begins');
  const endDate = watch('ends');
  const make = watch('carMakeId');
  const model = watch('carModelId');
  const carId = watch('carId');

  useEffect(() => {
    if (reservationId !== undefined && state.reservation.id === undefined) {
      state.loadReservation(Number(reservationId));
    }
    if (carId === undefined && vehicleId) {
      const car = appState.allCars.find((car) => vehicleId && car?.carId === Number(vehicleId));
      car && state.saveCar({...car});
      car?.carId && state.loadAllCarReservations(car.carId);
    }
  }, [state]);

  useEffect(() => {
    reset({
      carId: Number(vehicleId),
      ...reservation,
      begins: reservation.begins ?? new Date(),
      ends: reservation.ends ?? new Date(),
    });
  }, [reservation]);

  useEffect(() => {
    if (state.item.id !== undefined) reset(reservation);
  }, [state.item, reservationId, reset, reservation]);

  useEffect(() => {
    if (startDate && endDate) state.getAvailableCars(startDate, endDate, make, model);
  }, [startDate, endDate, make, model]);

  const modelOptions = useMemo(() => {
    if (commonData?.carMakesWithModels && make) {
      const markData = commonData.carMakesWithModels.find((item) => item.id === make);
      return markData?.models?.map((model) => ({
        value: model.carModelId,
        label: model.modelName,
      }));
    }
  }, [commonData?.carMakesWithModels, make]);

  usePrompt(t('Are you sure? There are unsaved changes.'), reservationId ? isDirty : false);

  return (
    <>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => browserHistory.push(AppRoutePaths.reservations.url)}
        style={{color: token.colorTextDescription}}
        className="px-0"
      >
        {t('Back')}
      </Button>
      <div className="px-48 max-w-[92rem] mx-auto">
        <FormShell
          isDirty={isDirty}
          onSubmitLabel={t('Save')}
          onReset={() => reset()}
          onSubmit={handleSubmit((payload) => {
            state.save(payload);
          })}
          onDelete={reservationId ? () => state.deleteReservation() : undefined}
          isLoading={state.isLoading}
        >
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2 col-span-2">
            <Controller
              name="begins"
              control={control}
              rules={{required: true}}
              render={() => (
                <RangeInput
                  id="startDateTime"
                  className={'col-start-1'}
                  label={t('Start and end date')}
                  value={[startDate, endDate]}
                  required
                  isAllDay
                  showTime
                  error={errors.begins ?? errors.ends}
                  disabledDates={state.carReservationsDatesWithoutCurrent}
                  onChange={(startDate, endDate) => {
                    setValue('begins', startDate);
                    setValue('ends', endDate);
                  }}
                />
              )}
            />
            {!(vehicleId || reservation.carId) ? (
              <>
                <Controller
                  name="carMakeId"
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <Select
                      id="carMakeId"
                      className="col-start-1"
                      label={t('Brand')}
                      options={appState.addedCarsMarkOptions}
                      value={appState.addedCarsMarkOptions?.find((item) => item.value === value) ?? null}
                      onChange={(e) => {
                        onChange((e as OptionType).value);
                        setValue('carModelId', undefined);
                      }}
                    />
                  )}
                />
                <Controller
                  name="carModelId"
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <Select
                      id="carModelId"
                      label={t('Model')}
                      className="col-start-1"
                      options={modelOptions}
                      value={modelOptions?.find((item) => item.value === value) ?? null}
                      onChange={(e) => onChange((e as OptionType).value)}
                    />
                  )}
                />
              </>
            ) : (
              <TextInput
                label="Car"
                disabled
                containerClassName="col-start-1"
                value={`${state.reservation?.car?.carMakeName} ${state.reservation?.car?.carModelName} (${state.reservation?.car?.plateNumber})`}
              />
            )}
            {!reservation.carId && (
              <Controller
                name="carId"
                control={control}
                rules={{required: true}}
                render={({field: {onChange, value}}) => (
                  <Select
                    id="carId"
                    label={t('Search car')}
                    className="col-start-1"
                    placeholder={availableCarsOptions.length > 0 ? t('Select car') : t('No available cars')}
                    isDisabled={availableCarsOptions.length === 0}
                    options={availableCarsOptions}
                    value={availableCarsOptions.find((item) => item.value === value) ?? null}
                    required
                    error={
                      errors.carId ??
                      ((availableCarsOptions.length === 0 && {
                        message: t('There are no vehicles met selected criteria.'),
                      }) as FieldError)
                    }
                    onChange={(option) => onChange((option as OptionType).value)}
                  />
                )}
              />
            )}
            <Controller
              name="customerId"
              control={control}
              rules={{required: true}}
              render={({field: {onChange, value}}) => (
                <Select
                  id="customerId"
                  label={t('Customer')}
                  className="col-start-1"
                  options={customerOptions}
                  value={customerOptions?.find((item) => item.value === value) ?? null}
                  required={true}
                  error={errors.carModelId}
                  onChange={(e) => onChange((e as OptionType).value)}
                />
              )}
            />
            <div className="col-start-1">
              <Controller
                name="description"
                control={control}
                render={({field: {onChange, value}}) => (
                  <TextArea
                    label={t('Description')}
                    id={'description'}
                    className="col-start-1"
                    value={value ?? ''}
                    placeholder={t('Description')}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
        </FormShell>
      </div>
    </>
  );
});
